import { History, Leaf, ListTodo } from 'lucide-react';

import { ShipmentEmissionsStatement } from '@/components/modules/shipment/emissions-statement';
import { ShipmentError } from '@/components/modules/shipment/error';
import { ShipmentHistoryList } from '@/components/modules/shipment/history-list';
import { ShipmentInfoCard } from '@/components/modules/shipment/info-card';
import { ShipmentJourney } from '@/components/modules/shipment/journey';
import { ShipmentLoadingSummary } from '@/components/modules/shipment/loading/summary';
import { ShipmentMap } from '@/components/modules/shipment/map';
import { ShipmentMilestoneList } from '@/components/modules/shipment/milestone/list';
import { ShipmentNotFound } from '@/components/modules/shipment/not-found';
import { ShipmentMetaSummary } from '@/components/modules/shipment/shipment-meta-summary';
import { ShipmentSummary } from '@/components/modules/shipment/summary';
import { ShipmentTimes } from '@/components/modules/shipment/times';
import { Skeleton } from '@/components/ui/loading/skeleton';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/tabs';
import { useSavedItems } from '@/hooks/use-saved-items';
import { useShipmentMeta } from '@/hooks/use-shipment-meta';
import { SAVED_ITEM_TYPE, SHIPMENT_STATUS, SHIPMENT_SUB_STATUS } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { SHIPMENT_TYPES, ShipmentCargo, ShipmentModel } from '@/lib/models/shipment/types';
import { getShipmentIdTypeLabel } from '@/lib/models/shipment/utils/get-shipment-id-type';
import { ShareVariant } from '@/types';
import { EmissionsData } from '@/types/api-response-types';
import { parseShipmentTrackingData } from '@/utils/shipments';
import { tv } from '@/utils/styles';

export interface ShipmentDetailProps {
  shipment: ShipmentModel | null;
  emissions?: {
    emissionsData?: EmissionsData;
    isLoading: boolean;
    canSeeEmissions?: boolean;
    cargo?: ShipmentCargo | ShipmentCargo[];
    updatedAt: string;
  };
  currentTab?: string;
  onTabChange?: (value: string) => void;
  isLoading?: boolean;
  isRefreshing?: boolean;
  error?: string;
  onBack?: () => void;
  onRequestRefresh?: () => void;
  className?: string;
  variant?: ShareVariant;
  onClickDetails?: (value: ShipmentModel) => void;
}

const ShipmentDetail = ({
  shipment,
  emissions,
  isLoading,
  isRefreshing,
  error,
  currentTab,
  onTabChange,
  onBack,
  onRequestRefresh,
  className,
  variant = 'default',
  onClickDetails,
}: ShipmentDetailProps) => {
  const isCollection = Boolean(shipment?.associatedContainers?.length);
  const isParcel = shipment?.type === SHIPMENT_TYPES.PARCEL;
  const { shipmentMeta } = useShipmentMeta({ shipmentId: shipment?.id });
  const shipmentId = shipment ? getShipmentIdTypeLabel(shipment) : null;
  const shouldShowEmissions = !isParcel;

  const { createSavedItem, getSavedItemByValue, deleteSavedItem, isEnabled: isSavedItemsEnabled } = useSavedItems();
  const savedItem = getSavedItemByValue(
    isCollection
      ? {
          key: 'mblNumber',
          value: shipmentId?.label,
          type: SAVED_ITEM_TYPE.SHIPMENT,
        }
      : { key: 'id', value: shipment?.id, type: SAVED_ITEM_TYPE.SHIPMENT },
  );

  const handleClickSavedItem = async () => {
    if (savedItem) {
      await deleteSavedItem(savedItem);
    } else {
      if (!shipmentId || !shipment) return;
      const data = isCollection ? { mblNumber: shipmentId.label } : { id: shipment.id };

      const newSavedItem: SavedItemModel = {
        label: shipmentId.label,
        type: SAVED_ITEM_TYPE.SHIPMENT,
        data: JSON.stringify(data),
      };
      await createSavedItem(newSavedItem);
    }
  };

  if (error) return <ShipmentError message={error} onBack={onBack} />;
  const trackingData = parseShipmentTrackingData(shipment?.trackingData);

  const hasData =
    shipment &&
    !error &&
    Boolean(
      trackingData &&
        trackingData?.status !== SHIPMENT_STATUS.NEW &&
        trackingData?.status !== SHIPMENT_STATUS.UNTRACKABLE,
    );
  const shouldShowLoading = isLoading && !shipment;
  const shipmentNotFound = !hasData && !isLoading;

  const { markers, trackingUpdatedAt, vehicles } = trackingData || {};

  const { base, header, body, summary, summaryDetails, milestones } = styles({ variant, shipmentNotFound });

  return (
    <div className={base({ className })}>
      <div className={header()}>
        {shouldShowLoading ? (
          <ShipmentLoadingSummary />
        ) : (
          <div className={summary()}>
            {shipment && (
              <ShipmentSummary
                shipment={shipment}
                onClickSavedItem={handleClickSavedItem}
                isSavedItemsEnabled={isSavedItemsEnabled}
                savedItem={savedItem}
                variant={variant}
                isRefreshing={isRefreshing}
                isLoading={isLoading}
                lastUpdatedDate={trackingUpdatedAt}
                onRequestRefresh={onRequestRefresh}
              />
            )}
            {shipmentNotFound ? (
              <div className="p-4 pb-12">
                <ShipmentNotFound onBack={onBack} />
              </div>
            ) : (
              <div className={summaryDetails()}>
                <ShipmentJourney journey={shipment?.journey} oldJourney={trackingData?.journey} />
                <ShipmentTimes
                  arrivalDates={{ eta: shipment?.eta, ata: shipment?.ata }}
                  departureDates={{ etd: shipment?.etd, atd: shipment?.atd }}
                  originalEta={shipment?.originalEta}
                  delay={shipment?.delay}
                  modeType={shipment?.type}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {!shipmentNotFound && (
        <div className={body()}>
          <div className={milestones()}>
            {shouldShowLoading ? (
              <div className="space-y-8 pt-4 opacity-50">
                <Skeleton className="h-6" />
                <Skeleton className="h-6" />
                <Skeleton className="h-6" />
              </div>
            ) : (
              <TabsRoot value={currentTab} onValueChange={onTabChange}>
                <TabsList>
                  <TabsTrigger value="milestones">
                    <ListTodo className="size-4" /> Milestones
                  </TabsTrigger>
                  <TabsTrigger value="history">
                    <History className="size-4" /> History
                  </TabsTrigger>
                  {shouldShowEmissions && (
                    <TabsTrigger value="emissions">
                      <Leaf className="size-4 fill-green-200" />
                      Emissions
                    </TabsTrigger>
                  )}
                </TabsList>
                <TabsContent value="milestones" className="mt-6">
                  <ShipmentMilestoneList
                    milestones={shipment?.milestones}
                    isInTransit={shipment?.subStatus === SHIPMENT_SUB_STATUS.IN_TRANSIT}
                  />
                </TabsContent>
                <TabsContent value="history" className="mt-4">
                  <ShipmentHistoryList shipmentId={shipment?.id} />
                </TabsContent>
                {shouldShowEmissions && (
                  <TabsContent value="emissions" className="mt-8">
                    {shipment && (
                      <ShipmentEmissionsStatement
                        emissionsData={emissions?.emissionsData}
                        isLoading={emissions?.isLoading}
                        canSeeEmissions={emissions?.canSeeEmissions}
                        cargo={emissions?.cargo}
                        updatedAt={emissions?.updatedAt}
                      />
                    )}
                  </TabsContent>
                )}
              </TabsRoot>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {hasData ? (
              <>
                {!isParcel && (
                  <ShipmentMap
                    journey={shipment.journey}
                    milestones={shipment.milestones}
                    markers={markers || []}
                    vehicles={vehicles || []}
                    type={shipment.type}
                  />
                )}
                {trackingData && <ShipmentInfoCard shipment={shipment} onClickDetails={onClickDetails} />}
                {shipmentMeta && <ShipmentMetaSummary shipmentMeta={shipmentMeta} />}
              </>
            ) : null}
            {shouldShowLoading ? <Skeleton className="h-[450px]" /> : null}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'grid gap-4',
    summary: 'rounded border border-grey-300',
    summaryDetails: 'grid min-h-[160px] gap-4 p-4',
    header: 'rounded bg-white',
    body: 'grid grid-cols-1 gap-4 lg:grid-cols-[6fr_4fr]',
    milestones: 'rounded border border-grey-300 bg-white px-4 pb-4 pt-1',
  },
  variants: {
    variant: {
      default: {},
      shared: {
        summary: 'shadow-soft',
      },
    },
    shipmentNotFound: {
      true: {
        milestones: 'col-span-6',
      },
    },
  },
});

export { ShipmentDetail };
